/** THE ISLAND STORE (client caching stage 2; Tom 2026-10-19, pre-merge; wire
 *  contract §6). Shape B (Tom, 2026-10-20: "YEs, do B"): for each island, a
 *  HEAD — the base its load started from (the manifest's packs, which the
 *  pack store already keeps) and the state cursor — and the LIST of patches
 *  the page applied after that base (the commits after it, the draft's
 *  segments, the tail and every live edit since), kept in IndexedDB. A
 *  refresh draws the base from the pack store, replays the list, and catches
 *  up through the tail from the cursor. Shape A kept the island's assembled
 *  rows instead: every record a second time beside its packs, rewritten
 *  whole on every write (13.5 MB against B's 8.9 MB at 10k; the benchmark,
 *  2026-10-20).
 *
 *  Rules (design note agreed with VC-core and VC-Authz, 2026-10-19):
 *  - One database per (User node id, organisation): `sp-island-state:<user>:<org>`.
 *    No organisation, or impersonating: nothing is stored (memory only).
 *  - One entry per (island, lane), for a committed line or the reader's OWN
 *    draft (`wip/<me>[//<branch>]`); never a proposal, another person's lane,
 *    or an island read AT a pin.
 *  - An entry is used only when the open answers the same field set, lane,
 *    draft gen and segment epoch, and it was written by this client build
 *    (STORE_FORMAT plus the generated schema version). Otherwise, and on any
 *    410, 403 or 404 for its island, it is deleted.
 *  - Sign-out, another user, or a 401: every database of the user goes, and
 *    other tabs are told (they reload).
 *  - Bounded by bytes (heads and lists, serialised); past the bound the
 *    entries opened least recently go first. Every failure — no IndexedDB,
 *    quota, a broken database — degrades to "not stored".
 *  Nothing stored is ever sent to the server: the open runs on every load,
 *  and every write goes through the normal judged path. */

import { versioningManifest } from "@sourcepilot/generated/api-manifest";
import { closePackCachesOf, deleteDatabases, deletePackCachesOf } from "../state-packs/pack-cache";
import { fromRest, toRest } from "./at-rest";
import { getServingIdentity, isServingImpersonating, onServingIdentityChange } from "../dag-sync/serving-identity";

/** Bump whenever what an entry holds, or how it is read back, changes: an
 *  entry written by another format is never used (the shape test pins it).
 *  2: shape B (a head and its list). 3: a committed line's entry stands
 *  only on the base the open answers, and every entry a list could have
 *  lost patches from (before 2026-10-27) is discarded. */
export const STORE_FORMAT = 3;

/** The client build an entry was written by. */
export const CLIENT_BUILD = `${STORE_FORMAT}:${(versioningManifest as { schema_version?: string }).schema_version ?? ""}`;

/** Per database, beside the 200 MB pack store. */
export const ISLAND_STORE_BYTES = 100 * 1024 * 1024;

/** Whether a refresh DRAWS a stored island before the open answers (instant
 *  paint). Tom, 2026-10-19: "Revoked-access flash - acceptable for now": a
 *  revoked grant may show briefly, then the page clears and the entry is
 *  deleted when the open refuses it. "For now": false uses an entry only
 *  once the open has answered for it (no flash; still no packs, commit files
 *  or segments). */
export const DRAW_BEFORE_OPEN = true;

/** CUT-OFFS (reviews, 2026-10-19; Tom 2026-10-20 for the list): a stored
 *  cursor never expires (ops are never pruned, VC-D), and a list grows with
 *  every edit. Past any bound the entry goes and the island loads from its
 *  newest snapshot (its packs are still reused from the pack store); that
 *  load records a new head, so the next refresh draws again:
 *  - the cursor (a watermark in ms) more than STORE_MAX_BEHIND_MS behind the
 *    open's: 7 days, the enrichment channels' tombstone horizon, past which
 *    they reload their bases anyway;
 *  - the tail answering more than STORE_MAX_CATCHUP_OPS ops, counted before
 *    any is applied;
 *  - the list replaying more than STORE_MAX_CATCHUP_OPS ops (a patch counts
 *    its ops, at least one; replaying costs about 18 ms per thousand at 10k:
 *    bench5). */
export const STORE_MAX_BEHIND_MS = 7 * 24 * 60 * 60 * 1000;
export const STORE_MAX_CATCHUP_OPS = 2_000;

/** Whether a stored cursor is close enough to the open's to catch up. */
export function withinCatchUp(storedCursor: number, openCursor: number): boolean {
  return Number.isFinite(storedCursor) && Number.isFinite(openCursor) && openCursor - storedCursor <= STORE_MAX_BEHIND_MS;
}

/** The base a head starts from: the load's manifest, by its packs' names
 *  (the pack store holds their rows). */
export type StoredBase = { commit: string | null; root: string; packs: { name: string; kind: string }[] };

/** A patch of the list, as the page applied it (after its filters). */
export type StoredPatch = Record<string, unknown>;

/** An island's head: what its list is relative to. */
export type StoredIsland = {
  format: number;
  build: string;
  island: string;
  lane: string;
  line: string;
  gen: string | null;
  epoch: string | null;
  fs: string;
  /** the state cursor the base plus the list are a cut at */
  cursor: number;
  base: StoredBase;
  /** patches in the list */
  count: number;
};

/** A head with its list, as read back, and the ops a replay applies. */
export type HeldIsland = StoredIsland & { ops: StoredPatch[]; replay: number };

/** The ops a patch carries for the cut-off: its ops, at least one. */
export function opsIn(patch: StoredPatch): number {
  const ops = (patch as { ops?: unknown }).ops;
  return Array.isArray(ops) && ops.length > 0 ? ops.length : 1;
}

export interface IslandStore {
  get(island: string, lane: string): Promise<HeldIsland | undefined>;
  /** a new head, and its list starts again with `ops` */
  putBase(head: StoredIsland, ops: StoredPatch[]): Promise<void>;
  /** `ops` applied since, after the list's first `from` patches, and the
   *  cursor they reach. Answers false when nothing was kept: no head, a list
   *  out of step with `from`, or replaying past STORE_MAX_CATCHUP_OPS — the
   *  entry then goes, and the next load records a new head. */
  append(island: string, lane: string, from: number, ops: StoredPatch[], cursor: number): Promise<boolean>;
  /** the lane the open last answered when the page asked for `asked` (a
   *  branch, or DEFAULT_LANE: my draft or main, the server picks) — what a
   *  refresh draws before the open answers */
  laneFor(island: string, asked: string): Promise<string | undefined>;
  setLaneFor(island: string, asked: string, lane: string | null): Promise<void>;
  /** one lane's entry, or every lane of the island */
  delete(island: string, lane?: string): Promise<void>;
}

/** What a page asks for when it names no branch. */
export const DEFAULT_LANE = "@default";

export const noIslandStore: IslandStore = {
  async get() { return undefined; },
  async putBase() {},
  async append() { return false; },
  async laneFor() { return undefined; },
  async setLaneFor() {},
  async delete() {},
};

const PREFIX = "sp-island-state:";
const ISLANDS = "islands"; // `<island>|<lane>` -> StoredIsland (the head)
const OPS = "ops"; // `<island>|<lane>|<seq>` -> StoredPatch (the list)
const USAGE = "usage"; // `<island>|<lane>` -> Usage: the head and its list
const META = "meta"; // "build" -> CLIENT_BUILD
const LANES = "lanes"; // `<island>|<asked>` -> the lane the open answered
const REGISTRY = "sp-island-state:databases"; // localStorage: the names opened here
const LAST_USER = "sp-island-state:last-user";
/** localStorage: users signed out here whose databases (island state and
 *  packs) a later page must delete — kept until that deletion is done */
const SIGNED_OUT = "sp-island-state:signed-out";

/** Users signed out in this page: their island databases never open again
 *  here. The page lives on until its redirect, and the store's next write
 *  (every patch it still hears) would re-create a database the sign-out
 *  deleted (security, VC-e2e tier 1, 2026-10-26). */
const signedOut = new Set<string>();

const signedOutUsers = (): string[] => {
  try { return (JSON.parse(localStorage.getItem(SIGNED_OUT) ?? "[]") as string[]).filter((u) => typeof u === "string" && !!u); } catch { return []; }
};
function setSignedOutUsers(users: string[]): void {
  try {
    if (users.length) localStorage.setItem(SIGNED_OUT, JSON.stringify(Array.from(new Set(users))));
    else localStorage.removeItem(SIGNED_OUT);
  } catch { /* none */ }
}

const keyOf = (island: string, lane: string) => `${island}|${lane}`;
/** Beside each head, in the clear (none of it is the island's content): its
 *  bytes and last use for the bound, and the list's length and the cursor it
 *  reaches, which every append moves. The head itself is written once, at
 *  rest, by putBase: an append never decodes or re-encodes it inside its
 *  transaction (a transaction ends at the first wait that is not its own). */
type Usage = { bytes: number; used: number; count: number; cursor: number; replay: number };
const opKey = (island: string, lane: string, seq: number) => `${keyOf(island, lane)}|${String(seq).padStart(9, "0")}`;

export const userOf = (identity: string | null | undefined) => (identity ? identity.slice(0, identity.lastIndexOf("|")) : "");
export const orgOf = (identity: string | null | undefined) => (identity ? identity.slice(identity.lastIndexOf("|") + 1) : "");

/** A lane whose assembled state may be kept: a committed line, or my own
 *  draft. Never a proposal or another person's draft. */
export function storableLane(lane: string | null | undefined, me: string): boolean {
  if (!lane) return false;
  if (lane.startsWith("proposal/")) return false;
  if (lane.startsWith("wip/")) return !!me && (lane === `wip/${me}` || lane.startsWith(`wip/${me}//`));
  return true;
}

/** Whether an entry may stand for what the open answered: the same field
 *  set (one per answer), lane and line; for a DRAFT also the same gen and
 *  segment epoch (VC-D, 2026-10-19: within one gen a draft's ops only grow;
 *  a re-base, re-open, removal or new draft changes it). A LINE is never
 *  matched on its gen: `<head>.line` moves with every commit, and a line's
 *  tail by cursor is complete. */
export function entryMatches(
  entry: StoredIsland | undefined,
  answer: { fs: string; lane: string; line: string; gen: string | null; epoch: string | null },
): entry is StoredIsland {
  if (!entry || entry.format !== STORE_FORMAT || entry.build !== CLIENT_BUILD) return false;
  if (entry.fs !== answer.fs || entry.lane !== answer.lane || entry.line !== answer.line) return false;
  if (!isDraftLane(entry.lane)) return true;
  return (entry.gen ?? null) === (answer.gen ?? null) && (entry.epoch ?? null) === (answer.epoch ?? null);
}

export const isDraftLane = (lane: string) => lane.startsWith("wip/");

function dbName(userId: string, organisationId: string): string {
  return `${PREFIX}${userId}:${organisationId}`;
}

function remember(name: string): void {
  try {
    const list = JSON.parse(localStorage.getItem(REGISTRY) ?? "[]") as string[];
    if (!list.includes(name)) localStorage.setItem(REGISTRY, JSON.stringify([...list, name]));
  } catch { /* no storage: the databases list still finds them */ }
}

/** IndexedDB, one database per (user, organisation), LRU by bytes. */
export function indexedDbIslandStore(userId: string, organisationId: string, maxBytes = ISLAND_STORE_BYTES): IslandStore {
  let opened: Promise<IDBDatabase | null> | null = null;
  const name = dbName(userId, organisationId);
  const done = (t: IDBTransaction) => new Promise<void>((ok) => { t.oncomplete = () => ok(); t.onerror = t.onabort = () => ok(); });
  const read = <T>(r: IDBRequest<T>) => new Promise<T | undefined>((ok) => { r.onsuccess = () => ok(r.result); r.onerror = () => ok(undefined); });
  // one island's list, in order: a key range in a browser, a prefix filter
  // where there is none (the unit tests' fake)
  const listKeys = async (ops: IDBObjectStore, prefix: string): Promise<string[]> => {
    if (typeof IDBKeyRange !== "undefined") {
      return (((await read(ops.getAllKeys(IDBKeyRange.bound(prefix, `${prefix}\uffff`)))) ?? []) as string[]);
    }
    return (((await read(ops.getAllKeys())) ?? []) as string[]).map(String).filter((k) => k.startsWith(prefix)).sort();
  };
  const dropEntry = async (t: IDBTransaction, k: string) => {
    t.objectStore(ISLANDS).delete(k);
    t.objectStore(USAGE).delete(k);
    for (const key of await listKeys(t.objectStore(OPS), `${k}|`)) t.objectStore(OPS).delete(key);
  };
  // past the bound, the entries opened least recently go first
  const evict = async (t: IDBTransaction, keep: string) => {
    const usageStore = t.objectStore(USAGE);
    const keys = ((await read(usageStore.getAllKeys())) ?? []) as string[];
    const usages = ((await read(usageStore.getAll())) ?? []) as Usage[];
    let total = usages.reduce((a, u) => a + (u?.bytes ?? 0), 0);
    const oldestFirst = keys.map((key, i) => ({ key, u: usages[i] })).sort((a, b) => (a.u?.used ?? 0) - (b.u?.used ?? 0));
    for (const { key, u } of oldestFirst) {
      if (total <= maxBytes) break;
      if (key === keep) continue;
      await dropEntry(t, key);
      total -= u?.bytes ?? 0;
    }
  };
  const open = (): Promise<IDBDatabase | null> => {
    if (opened) return opened;
    opened = new Promise((resolve) => {
      try {
        if (typeof indexedDB === "undefined" || signedOut.has(userId)) return resolve(null);
        // 2: shape B adds the list's store; an older database starts again
        const req = indexedDB.open(name, 2);
        req.onupgradeneeded = () => {
          const db = req.result;
          for (const s of Array.from(db.objectStoreNames)) db.deleteObjectStore(s);
          db.createObjectStore(ISLANDS);
          db.createObjectStore(OPS);
          db.createObjectStore(USAGE);
          db.createObjectStore(META);
          db.createObjectStore(LANES);
        };
        req.onsuccess = async () => {
          const db = req.result;
          // signed out while it opened: not used
          if (signedOut.has(userId)) { db.close(); opened = null; return resolve(null); }
          // a deletion (sign-out, another user) waits for every open
          // connection to close: close at once, and open again if used
          // (never for a user signed out here)
          db.onversionchange = () => { db.close(); opened = null; };
          remember(name);
          // another client build wrote it: start again
          try {
            const t = db.transaction([ISLANDS, OPS, USAGE, META, LANES], "readwrite");
            const build = await read(t.objectStore(META).get("build"));
            if (build !== CLIENT_BUILD) {
              t.objectStore(ISLANDS).clear();
              t.objectStore(OPS).clear();
              t.objectStore(USAGE).clear();
              t.objectStore(LANES).clear();
              t.objectStore(META).put(CLIENT_BUILD, "build");
            }
            await done(t);
          } catch { /* a broken database: used as it is, entries still check their build */ }
          resolve(db);
        };
        req.onerror = () => resolve(null);
        req.onblocked = () => resolve(null);
      } catch {
        resolve(null);
      }
    });
    return opened;
  };
  return {
    async get(island, lane) {
      const db = await open();
      if (!db) return undefined;
      try {
        const k = keyOf(island, lane);
        const t = db.transaction([ISLANDS, OPS, USAGE], "readwrite");
        const kept = await read(t.objectStore(ISLANDS).get(k));
        let list: unknown[] = [];
        const usage = kept ? ((await read(t.objectStore(USAGE).get(k))) as Usage | undefined) : undefined;
        if (usage) {
          t.objectStore(USAGE).put({ ...usage, used: Date.now() }, k); // used: now the newest
          const ops = t.objectStore(OPS);
          list = await Promise.all((await listKeys(ops, `${k}|`)).map((key) => read(ops.get(key))));
        }
        await done(t);
        if (!usage) return undefined;
        const head = await fromRest<StoredIsland>(kept);
        if (!head || typeof head !== "object") return undefined;
        const ops = await Promise.all(list.map((v) => fromRest<StoredPatch>(v)));
        // a list that does not read back whole is no list: not used
        if (ops.length !== usage.count || ops.some((o) => !o || typeof o !== "object")) return undefined;
        return { ...head, count: usage.count, cursor: usage.cursor, ops: ops as StoredPatch[], replay: usage.replay };
      } catch {
        return undefined;
      }
    },
    async putBase(head, list) {
      let bytes: number;
      let value: unknown;
      let values: unknown[];
      const entry: StoredIsland = { ...head, count: list.length };
      const replay = list.reduce((a, o) => a + opsIn(o), 0);
      try {
        bytes = JSON.stringify(entry).length + list.reduce((a, o) => a + JSON.stringify(o).length, 0);
        value = await toRest(entry);
        values = await Promise.all(list.map((o) => toRest(o)));
      } catch { return; }
      const db = await open();
      if (!db) return;
      const k = keyOf(head.island, head.lane);
      try {
        const t = db.transaction([ISLANDS, OPS, USAGE], "readwrite");
        // the old head and list go either way: they no longer stand for the island
        await dropEntry(t, k);
        if (bytes > maxBytes || replay > STORE_MAX_CATCHUP_OPS) { await done(t); return; }
        t.objectStore(ISLANDS).put(value, k);
        values.forEach((v, i) => t.objectStore(OPS).put(v, opKey(head.island, head.lane, i)));
        t.objectStore(USAGE).put({ bytes, used: Date.now(), count: list.length, cursor: head.cursor, replay } satisfies Usage, k);
        await evict(t, k);
        await done(t);
      } catch { /* quota or a broken database: not stored */ }
    },
    async append(island, lane, from, list, cursor) {
      if (list.length === 0) return true;
      let added: number;
      let values: unknown[];
      const more = list.reduce((a, o) => a + opsIn(o), 0);
      try {
        added = list.reduce((a, o) => a + JSON.stringify(o).length, 0);
        values = await Promise.all(list.map((o) => toRest(o)));
      } catch { return false; }
      const db = await open();
      if (!db) return false;
      const k = keyOf(island, lane);
      try {
        const t = db.transaction([ISLANDS, OPS, USAGE], "readwrite");
        const usage = (await read(t.objectStore(USAGE).get(k))) as Usage | undefined;
        const count = from + list.length;
        // no head, a list out of step (a write lost, another tab), past the
        // cut-off, or past the bound: the entry goes, and the next load
        // records a new head
        if (!usage || usage.count !== from || usage.replay + more > STORE_MAX_CATCHUP_OPS || usage.bytes + added > maxBytes) {
          await dropEntry(t, k);
          await done(t);
          return false;
        }
        values.forEach((v, i) => t.objectStore(OPS).put(v, opKey(island, lane, from + i)));
        t.objectStore(USAGE).put({ bytes: usage.bytes + added, used: Date.now(), count, cursor, replay: usage.replay + more } satisfies Usage, k);
        await evict(t, k);
        await done(t);
        return true;
      } catch {
        return false;
      }
    },
    async laneFor(island, asked) {
      const db = await open();
      if (!db) return undefined;
      try {
        const t = db.transaction([LANES], "readonly");
        const lane = await read(t.objectStore(LANES).get(keyOf(island, asked)));
        await done(t);
        return typeof lane === "string" ? lane : undefined;
      } catch {
        return undefined;
      }
    },
    async setLaneFor(island, asked, lane) {
      const db = await open();
      if (!db) return;
      try {
        const t = db.transaction([LANES], "readwrite");
        if (lane) t.objectStore(LANES).put(lane, keyOf(island, asked));
        else t.objectStore(LANES).delete(keyOf(island, asked));
        await done(t);
      } catch { /* not stored */ }
    },
    async delete(island, lane) {
      const db = await open();
      if (!db) return;
      try {
        const t = db.transaction([ISLANDS, OPS, USAGE, LANES], "readwrite");
        if (lane !== undefined) {
          await dropEntry(t, keyOf(island, lane));
        } else {
          const keys = ((await read(t.objectStore(USAGE).getAllKeys())) ?? []) as string[];
          const all = ((await read(t.objectStore(ISLANDS).getAllKeys())) ?? []) as string[];
          for (const key of new Set([...keys, ...all])) {
            if (String(key).startsWith(`${island}|`)) await dropEntry(t, String(key));
          }
          // a list without its head (a write cut short) goes too
          for (const key of await listKeys(t.objectStore(OPS), `${island}|`)) t.objectStore(OPS).delete(key);
          // and what it was drawn by: the island is not drawn before an open again
          const pointers = ((await read(t.objectStore(LANES).getAllKeys())) ?? []) as string[];
          for (const key of pointers) if (String(key).startsWith(`${island}|`)) t.objectStore(LANES).delete(key);
        }
        await done(t);
      } catch { /* a broken database: the entry's own checks still hold */ }
    },
  };
}

/** The island store for who the data is read as. Nothing is stored without
 *  a User node id or an organisation, or while impersonating (VC-Authz, 2026-10-19: keyed by
 *  the impersonated person, an admin's browser would hold their data). */
export function islandStoreFor(identity: string | null | undefined, opts: { impersonating?: boolean; byNodeId?: boolean } = {}): IslandStore {
  const user = userOf(identity);
  const org = orgOf(identity);
  // never keyed by an email: no User node id, nothing stored (VC-Authz)
  if (!user || !org || opts.impersonating || !opts.byNodeId) return noIslandStore;
  return indexedDbIslandStore(user, org);
}

async function databaseNames(): Promise<string[]> {
  const names = new Set<string>();
  try { for (const n of JSON.parse(localStorage.getItem(REGISTRY) ?? "[]") as string[]) names.add(n); } catch { /* none */ }
  try {
    const listed = await (indexedDB as unknown as { databases?: () => Promise<{ name?: string }[]> }).databases?.();
    for (const d of listed ?? []) if (d.name?.startsWith(PREFIX)) names.add(d.name);
  } catch { /* not offered */ }
  return Array.from(names);
}

/** Delete every island database of `userId` (all its organisations).
 *  True when every one known was confirmed deleted; a blocked or failed
 *  one keeps its registry name, so a later clean-up still finds it
 *  without enumeration (Codex F6, 2026-10-26). */
export async function deleteIslandStoresOf(userId: string): Promise<boolean> {
  if (!userId || typeof indexedDB === "undefined") return true;
  const mine = (await databaseNames()).filter((n) => n.startsWith(`${PREFIX}${userId}:`));
  const deleted = await deleteDatabases(mine);
  forgetRegistered(deleted);
  return deleted.length === mine.length;
}

/** Registry names of databases confirmed deleted, and only those. */
function forgetRegistered(deleted: string[]): void {
  if (!deleted.length) return;
  try {
    const left = (JSON.parse(localStorage.getItem(REGISTRY) ?? "[]") as string[]).filter((n) => !deleted.includes(n));
    localStorage.setItem(REGISTRY, JSON.stringify(left));
  } catch { /* none */ }
}

// ---- who the data is read as, across tabs ----

const CHANNEL = "sp-identity";
let lastUser: string | null = null;
let reloading = false;
let reloadPage: () => void = () => { try { window.location.reload(); } catch { /* not a page */ } };
let channel: BroadcastChannel | null = null;

function tellOtherTabs(user: string): void {
  try { channel?.postMessage({ type: "identity", user }); } catch { /* closed */ }
}

/** Another tab says who it now reads as. A tab reading as someone else
 *  (another user, or nobody: a sign-out) holds what it may no longer show:
 *  it reloads. */
function heardFromAnotherTab(user: string | null | undefined): void {
  if (lastUser === null) return; // this page has no identity yet: it will read the new one
  if ((user ?? "") !== lastUser && !reloading) { reloading = true; reloadPage(); }
}

function listen(): void {
  if (channel || typeof window === "undefined") return;
  try {
    if (typeof BroadcastChannel !== "undefined") {
      channel = new BroadcastChannel(CHANNEL);
      channel.onmessage = (e: MessageEvent) => { if (e.data?.type === "identity") heardFromAnotherTab(e.data.user); };
    }
  } catch { channel = null; }
  // the fallback for a browser without BroadcastChannel: the last user key
  try {
    window.addEventListener("storage", (e: StorageEvent) => {
      if (e.key === LAST_USER) heardFromAnotherTab(e.newValue);
    });
  } catch { /* not a page */ }
}

/** Sign-out, another user, or impersonation starting or ending deletes the
 *  previous user's databases (island state and packs). Also run at a page's
 *  first identity: a previous user who never signed out here (a closed tab)
 *  is found through the last user remembered. */
export async function islandStoreIdentity(identity: string | null): Promise<void> {
  listen();
  const user = userOf(identity);
  let previous = lastUser;
  if (previous === null) {
    try { previous = localStorage.getItem(LAST_USER); } catch { previous = null; }
    // a page's first identity: what a sign-out here could not finish
    // deleting (blocked by another tab, cut short by the page leaving) goes
    // now, whoever signs in
    await deleteSignedOut();
  }
  const changed = previous !== null && previous !== user;
  if (previous && changed) await deleteIslandStoresOf(previous);
  lastUser = user;
  try {
    if (user) localStorage.setItem(LAST_USER, user);
    else localStorage.removeItem(LAST_USER);
  } catch { /* none */ }
  if (changed) tellOtherTabs(user);
}

onServingIdentityChange((identity) => { void islandStoreIdentity(identity); });

let organisationsChecked = false;
/** Once per page: the databases of organisations the user no longer belongs
 *  to go (VC-Authz, 2026-10-19). One could never be drawn — every open is
 *  judged, and the user cannot switch into that organisation — but it would
 *  otherwise stay at rest until sign-out. `listOrganisations` answers the
 *  user's organisation ids now (GET /auth/organisations), or null when it
 *  could not tell (then nothing is deleted, and the next page asks again). */
export async function forgetRemovedOrganisations(listOrganisations: () => Promise<string[] | null>): Promise<void> {
  const user = userOf(getServingIdentity());
  if (organisationsChecked || !user || isServingImpersonating() || typeof indexedDB === "undefined") return;
  organisationsChecked = true;
  const orgs = await listOrganisations().catch(() => null);
  if (!orgs) { organisationsChecked = false; return; }
  const prefix = `${PREFIX}${user}:`;
  // never the organisation the session acts as: it is held by definition
  // (an admin may act in one it holds no membership in, which the list
  // leaves out), and its store is what this page draws from
  const active = orgOf(getServingIdentity());
  const gone = (await databaseNames()).filter((n) => n.startsWith(prefix) && n.slice(prefix.length) !== active && !orgs.includes(n.slice(prefix.length)));
  const [deleted] = await Promise.all([deleteDatabases(gone), deletePackCachesOf(user, [...orgs, active])]); // and their pack databases
  forgetRegistered(deleted);
}

/** Sign-out, or a 401 (the session ended on the server): the user's island
 *  and pack databases go (at most `waitMs`, so a slow browser never holds
 *  the sign-out), and other tabs are told. */
export async function forgetIslandStoresAtSignOut(waitMs = 1500): Promise<void> {
  const who = userOf(getServingIdentity()) || lastUser || "";
  // never opened again in this page, and remembered until a later page has
  // deleted them (the deletion below may not finish before the page leaves)
  if (who) { signedOut.add(who); closePackCachesOf(who); setSignedOutUsers([...signedOutUsers(), who]); }
  await Promise.race([
    Promise.all([deleteIslandStoresOf(who), deletePackCachesOf(who)]),
    new Promise<void>((ok) => setTimeout(ok, waitMs)),
  ]);
  lastUser = "";
  try { localStorage.removeItem(LAST_USER); } catch { /* none */ }
  tellOtherTabs("");
}

/** The users a sign-out here left to delete: their island and pack
 *  databases go. Each is forgotten only when that is PROVEN — every
 *  deletion confirmed, or an enumeration that lists none of theirs; an
 *  enumeration unavailable or failed proves nothing, and a blocked or
 *  failed deletion is not a deletion (Codex F6, 2026-10-26). */
async function deleteSignedOut(): Promise<void> {
  const users = signedOutUsers();
  if (!users.length || typeof indexedDB === "undefined") return;
  const confirmed = await Promise.all(users.map(async (u) => (await Promise.all([deleteIslandStoresOf(u), deletePackCachesOf(u)])).every(Boolean)));
  const left = await allDatabaseNames();
  const theirs = (u: string) => (left ?? []).some((n) => n.startsWith(`${PREFIX}${u}:`) || n.startsWith(`sp-state-packs:${u}:`));
  setSignedOutUsers(users.filter((u, i) => !(confirmed[i] || (left !== null && !theirs(u)))));
}

/** Every database's name, or null when the browser cannot say (no
 *  `databases()`, or it failed): unknown, never "none". */
async function allDatabaseNames(): Promise<string[] | null> {
  try {
    const list = (indexedDB as unknown as { databases?: () => Promise<{ name?: string }[]> }).databases;
    if (typeof list !== "function") return null;
    return ((await list.call(indexedDB)) ?? []).map((d) => d.name ?? "").filter(Boolean);
  } catch { return null; }
}

let ended = false;
/** A 401 from the open, the tail or a channel: the session ended on the
 *  server (expired, or ended elsewhere). As a sign-out, once per page: what
 *  this browser keeps for the user goes, and other tabs are told
 *  (VC-Authz, 2026-10-19). */
export function sessionEnded(): void {
  if (ended) return;
  ended = true;
  void forgetIslandStoresAtSignOut();
}

/** tests only */
export function resetIslandStoreForTests(opts: { reload?: () => void } = {}): void {
  lastUser = null;
  signedOut.clear();
  reloading = false;
  ended = false;
  organisationsChecked = false;
  try { channel?.close(); } catch { /* closed */ }
  channel = null;
  if (opts.reload) reloadPage = opts.reload;
}
